import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { CourseFormatEntry } from "@/api/client";

// Legacy-IDs aus V0.2-Datensatz, die noch in bestehenden Courses stehen können.
// Die Anzeige übersetzt sie auf die neuen IDs, damit die Tabs konsistent bleiben.
const LEGACY_ALIASES: Record<string, string> = {
  lecture: "vorlesung",
  blended: "blended_learning",
  async_online: "selbstlernkurs",
};

function resolveId(id: string | null | undefined): string {
  if (!id) return "";
  return LEGACY_ALIASES[id] ?? id;
}

export interface CourseFormatPickerProps {
  value: string | null | undefined;
  onChange: (value: string) => void;
  formats: CourseFormatEntry[];
  disabled?: boolean;
}

// Zweistufige Format-Wahl: Kategorie-Tabs oben + Dropdown mit den Formaten der
// gewählten Kategorie. Defaultet auf die Kategorie des aktuellen Werts, sonst
// auf die erste Kategorie im Katalog.
export default function CourseFormatPicker({
  value,
  onChange,
  formats,
  disabled,
}: CourseFormatPickerProps) {
  const { t } = useTranslation();
  const categories = useMemo(() => {
    const seen = new Map<string, string>();
    for (const f of formats) {
      if (!seen.has(f.category)) seen.set(f.category, f.category_label);
    }
    return Array.from(seen.entries()).map(([id, label]) => ({ id, label }));
  }, [formats]);

  const resolvedValue = resolveId(value);
  const valueEntry = formats.find((f) => f.id === resolvedValue);
  const activeCategory =
    valueEntry?.category ?? categories[0]?.id ?? "";

  const formatsInCategory = formats.filter((f) => f.category === activeCategory);
  const activeEntry = valueEntry?.description ? valueEntry : null;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1.5">
        {categories.map((cat) => {
          const isActive = cat.id === activeCategory;
          return (
            <button
              key={cat.id}
              type="button"
              disabled={disabled}
              onClick={() => {
                // Bei Kategorie-Wechsel: nur dann Wert leeren, wenn der aktuelle
                // Wert nicht in der neuen Kategorie liegt. Erste Auswahl in der
                // Kategorie auswählen, damit das Dropdown nicht leer steht.
                const first = formats.find((f) => f.category === cat.id);
                if (first) onChange(first.id);
              }}
              className={`px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider transition ${
                isActive
                  ? "bg-ca-accent text-ca-brand"
                  : "bg-ca-card border border-ca-surface-border text-ca-surface-muted hover:text-ca-surface-ink"
              }`}
            >
              {cat.label}
            </button>
          );
        })}
      </div>
      <select
        value={resolvedValue}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white border border-ca-surface-border rounded-md px-4 py-3 text-ca-surface-ink outline-none focus:border-ca-accent"
      >
        <option value="">{t("common.chooseFormat")}</option>
        {formatsInCategory.map((f) => (
          <option key={f.id} value={f.id} title={f.description}>
            {f.name}
          </option>
        ))}
      </select>
      {activeEntry && (
        <p className="text-xs text-ca-surface-muted leading-relaxed">
          {activeEntry.description}
        </p>
      )}
    </div>
  );
}
